import { useSyncSharedTextColumns } from "@/hooks/use-sync-shared-text-columns";
import { LICENSE_SHARED_TEXT_COLUMNS } from "@/lib/shared-map-list-column-filters";
import {
  type NamedIdOption,
  columnFilterValuesEqual,
  reconcileLinkedColumnFilters,
  sameIdSet,
  withLinkedColumnValue,
} from "@/lib/sync-global-column-filters";
import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
} from "react";

export type LinkedColumnFilterBucket = {
  columnId: string;
  filters: Record<string, string[]>;
  setFilters: Dispatch<SetStateAction<Record<string, string[]>>>;
};

type DashboardListFilterState = {
  selectedJurisdictions: string[];
  setSelectedJurisdictions: (ids: string[]) => void;
  selectedLicenseTypes: string[];
  setSelectedLicenseTypes: (ids: string[]) => void;
  selectedRegions?: string[];
  setSelectedRegions?: (ids: string[]) => void;
  columnFilters: Record<string, string[]>;
  setColumnFilters: Dispatch<SetStateAction<Record<string, string[]>>>;
  inactiveRegistrationsColumnFilters: Record<string, string[]>;
  setInactiveRegistrationsColumnFilters: Dispatch<
    SetStateAction<Record<string, string[]>>
  >;
  unregisteredPropertiesColumnFilters: Record<string, string[]>;
  setUnregisteredPropertiesColumnFilters: Dispatch<
    SetStateAction<Record<string, string[]>>
  >;
  inspectionsColumnFilters: Record<string, string[]>;
  setInspectionsColumnFilters: Dispatch<
    SetStateAction<Record<string, string[]>>
  >;
  checksColumnFilters: Record<string, string[]>;
  setChecksColumnFilters: Dispatch<SetStateAction<Record<string, string[]>>>;
  invoicesColumnFilters: Record<string, string[]>;
  setInvoicesColumnFilters: Dispatch<SetStateAction<Record<string, string[]>>>;
  refundsColumnFilters: Record<string, string[]>;
  setRefundsColumnFilters: Dispatch<SetStateAction<Record<string, string[]>>>;
  titleRequestsColumnFilters?: Record<string, string[]>;
  setTitleRequestsColumnFilters?: Dispatch<
    SetStateAction<Record<string, string[]>>
  >;
};

function linkedBucket(
  columnId: string,
  filters: Record<string, string[]>,
  setFilters: Dispatch<SetStateAction<Record<string, string[]>>>,
): LinkedColumnFilterBucket {
  return { columnId, filters, setFilters };
}

/**
 * Keep one header multi-select aligned with the matching column on each list.
 * Setters are functional so a jurisdiction update and a license-type update
 * in the same turn both land on the same filter object.
 */
export function useSyncGlobalColumnFilters({
  enabled = true,
  selectedIds,
  setSelectedIds,
  options,
  buckets,
  adoptFromColumns = true,
}: {
  enabled?: boolean;
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
  options: readonly NamedIdOption[];
  buckets: readonly LinkedColumnFilterBucket[];
  adoptFromColumns?: boolean;
}) {
  const readyRef = useRef(false);
  const prevIdsRef = useRef(selectedIds);
  const prevColumnsRef = useRef<Array<string[] | undefined>>(
    buckets.map((bucket) => bucket.filters[bucket.columnId]),
  );
  const pendingRef = useRef<{
    ids: string[];
    columns: Array<string[] | undefined>;
  } | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const columns = buckets.map((bucket) => bucket.filters[bucket.columnId]);
    const catalogReady =
      selectedIds.length === 0 ||
      selectedIds.every((id) => options.some((option) => option.id === id));
    if (!catalogReady) return;

    if (pendingRef.current) {
      const pending = pendingRef.current;
      const caughtUp =
        sameIdSet(selectedIds, pending.ids) &&
        columns.length === pending.columns.length &&
        columns.every((values, index) =>
          columnFilterValuesEqual(values, pending.columns[index]),
        );
      if (!caughtUp) return;
      pendingRef.current = null;
      prevIdsRef.current = pending.ids;
      prevColumnsRef.current = pending.columns;
    }

    const previousIds = prevIdsRef.current;
    const previousColumns = prevColumnsRef.current;
    const initializing = !readyRef.current;
    const globalChanged = !sameIdSet(previousIds, selectedIds);
    const columnChanged = columns.map(
      (values, index) =>
        previousColumns.length !== columns.length ||
        !columnFilterValuesEqual(values, previousColumns[index]),
    );

    if (
      !initializing &&
      !globalChanged &&
      previousColumns.length === columns.length &&
      !columnChanged.some(Boolean)
    ) {
      return;
    }

    const result = reconcileLinkedColumnFilters({
      selectedIds,
      columns,
      options,
      globalChanged,
      columnChanged,
      adoptFromColumns,
      initializing,
    });
    readyRef.current = true;

    const idsChanged = !sameIdSet(selectedIds, result.selectedIds);
    let filtersChanged = false;
    buckets.forEach((bucket, index) => {
      const nextValues = result.columns[index];
      const nextFilters = withLinkedColumnValue(
        bucket.filters,
        bucket.columnId,
        nextValues,
      );
      if (nextFilters === bucket.filters) return;
      filtersChanged = true;
      bucket.setFilters((current) =>
        withLinkedColumnValue(current, bucket.columnId, nextValues),
      );
    });

    prevIdsRef.current = result.selectedIds;
    prevColumnsRef.current = result.columns;
    if (idsChanged || filtersChanged) {
      pendingRef.current = {
        ids: result.selectedIds,
        columns: result.columns,
      };
      if (idsChanged) setSelectedIds(result.selectedIds);
    }
  }, [
    adoptFromColumns,
    buckets,
    enabled,
    options,
    selectedIds,
    setSelectedIds,
  ]);
}

/** Operator and government lists that share the header jurisdiction and license type filters. */
export function useDashboardListFilterSync({
  state,
  jurisdictions,
  licenseTypes,
  regions = [],
  syncJurisdictions,
  includePaymentLists,
  includeTitleRequests = false,
}: {
  state: DashboardListFilterState;
  jurisdictions: readonly NamedIdOption[];
  licenseTypes: readonly NamedIdOption[];
  regions?: readonly NamedIdOption[];
  syncJurisdictions: boolean;
  includePaymentLists: boolean;
  includeTitleRequests?: boolean;
}) {
  const jurisdictionBuckets = useMemo(() => {
    const buckets = [
      linkedBucket(
        "jurisdiction.name",
        state.columnFilters,
        state.setColumnFilters,
      ),
      linkedBucket(
        "jurisdiction.name",
        state.inactiveRegistrationsColumnFilters,
        state.setInactiveRegistrationsColumnFilters,
      ),
      linkedBucket(
        "jurisdiction.name",
        state.unregisteredPropertiesColumnFilters,
        state.setUnregisteredPropertiesColumnFilters,
      ),
      linkedBucket(
        "jurisdiction_name",
        state.inspectionsColumnFilters,
        state.setInspectionsColumnFilters,
      ),
    ];
    if (
      includeTitleRequests &&
      state.titleRequestsColumnFilters &&
      state.setTitleRequestsColumnFilters
    ) {
      buckets.push(
        linkedBucket(
          "jurisdiction.name",
          state.titleRequestsColumnFilters,
          state.setTitleRequestsColumnFilters,
        ),
      );
    }
    if (includePaymentLists) {
      buckets.push(
        linkedBucket(
          "jurisdiction_name",
          state.checksColumnFilters,
          state.setChecksColumnFilters,
        ),
        linkedBucket(
          "jurisdiction_name",
          state.invoicesColumnFilters,
          state.setInvoicesColumnFilters,
        ),
        linkedBucket(
          "jurisdiction_name",
          state.refundsColumnFilters,
          state.setRefundsColumnFilters,
        ),
      );
    }
    return buckets;
  }, [
    includePaymentLists,
    includeTitleRequests,
    state.checksColumnFilters,
    state.columnFilters,
    state.inactiveRegistrationsColumnFilters,
    state.inspectionsColumnFilters,
    state.invoicesColumnFilters,
    state.refundsColumnFilters,
    state.setChecksColumnFilters,
    state.setColumnFilters,
    state.setInactiveRegistrationsColumnFilters,
    state.setInspectionsColumnFilters,
    state.setInvoicesColumnFilters,
    state.setRefundsColumnFilters,
    state.setTitleRequestsColumnFilters,
    state.setUnregisteredPropertiesColumnFilters,
    state.titleRequestsColumnFilters,
    state.unregisteredPropertiesColumnFilters,
  ]);

  const licenseTypeBuckets = useMemo(() => {
    const buckets = [
      linkedBucket(
        "license_type.name",
        state.columnFilters,
        state.setColumnFilters,
      ),
      linkedBucket(
        "license_type.name",
        state.inactiveRegistrationsColumnFilters,
        state.setInactiveRegistrationsColumnFilters,
      ),
      linkedBucket(
        "license_type.name",
        state.unregisteredPropertiesColumnFilters,
        state.setUnregisteredPropertiesColumnFilters,
      ),
      linkedBucket(
        "license_type_name",
        state.inspectionsColumnFilters,
        state.setInspectionsColumnFilters,
      ),
    ];
    if (includePaymentLists) {
      buckets.push(
        linkedBucket(
          "license_type_name",
          state.checksColumnFilters,
          state.setChecksColumnFilters,
        ),
        linkedBucket(
          "license_type_name",
          state.invoicesColumnFilters,
          state.setInvoicesColumnFilters,
        ),
        linkedBucket(
          "license_type_name",
          state.refundsColumnFilters,
          state.setRefundsColumnFilters,
        ),
      );
    }
    return buckets;
  }, [
    includePaymentLists,
    state.checksColumnFilters,
    state.columnFilters,
    state.inactiveRegistrationsColumnFilters,
    state.inspectionsColumnFilters,
    state.invoicesColumnFilters,
    state.refundsColumnFilters,
    state.setChecksColumnFilters,
    state.setColumnFilters,
    state.setInactiveRegistrationsColumnFilters,
    state.setInspectionsColumnFilters,
    state.setInvoicesColumnFilters,
    state.setRefundsColumnFilters,
    state.setUnregisteredPropertiesColumnFilters,
    state.unregisteredPropertiesColumnFilters,
  ]);

  useSyncGlobalColumnFilters({
    enabled: syncJurisdictions,
    selectedIds: state.selectedJurisdictions,
    setSelectedIds: state.setSelectedJurisdictions,
    options: jurisdictions,
    buckets: jurisdictionBuckets,
  });
  useSyncGlobalColumnFilters({
    selectedIds: state.selectedLicenseTypes,
    setSelectedIds: state.setSelectedLicenseTypes,
    options: licenseTypes,
    buckets: licenseTypeBuckets,
  });

  const regionBuckets = useMemo(() => {
    const buckets = [
      linkedBucket("regions", state.columnFilters, state.setColumnFilters),
      linkedBucket(
        "regions",
        state.inactiveRegistrationsColumnFilters,
        state.setInactiveRegistrationsColumnFilters,
      ),
      linkedBucket(
        "regions",
        state.unregisteredPropertiesColumnFilters,
        state.setUnregisteredPropertiesColumnFilters,
      ),
      linkedBucket(
        "regions",
        state.inspectionsColumnFilters,
        state.setInspectionsColumnFilters,
      ),
    ];
    if (
      includeTitleRequests &&
      state.titleRequestsColumnFilters &&
      state.setTitleRequestsColumnFilters
    ) {
      buckets.push(
        linkedBucket(
          "regions",
          state.titleRequestsColumnFilters,
          state.setTitleRequestsColumnFilters,
        ),
      );
    }
    return buckets;
  }, [
    includeTitleRequests,
    state.columnFilters,
    state.inactiveRegistrationsColumnFilters,
    state.inspectionsColumnFilters,
    state.setColumnFilters,
    state.setInactiveRegistrationsColumnFilters,
    state.setInspectionsColumnFilters,
    state.setTitleRequestsColumnFilters,
    state.setUnregisteredPropertiesColumnFilters,
    state.titleRequestsColumnFilters,
    state.unregisteredPropertiesColumnFilters,
  ]);
  useSyncGlobalColumnFilters({
    enabled: regions.length > 0 && !!state.setSelectedRegions,
    selectedIds: state.selectedRegions ?? [],
    setSelectedIds: state.setSelectedRegions ?? (() => undefined),
    options: regions,
    buckets: regionBuckets,
  });

  const sharedTextBuckets = useMemo(
    () => [
      {
        filters: state.columnFilters,
        setFilters: state.setColumnFilters,
        columns: LICENSE_SHARED_TEXT_COLUMNS,
      },
      {
        filters: state.inactiveRegistrationsColumnFilters,
        setFilters: state.setInactiveRegistrationsColumnFilters,
        columns: LICENSE_SHARED_TEXT_COLUMNS,
      },
      {
        filters: state.unregisteredPropertiesColumnFilters,
        setFilters: state.setUnregisteredPropertiesColumnFilters,
        columns: LICENSE_SHARED_TEXT_COLUMNS,
      },
    ],
    [
      state.columnFilters,
      state.inactiveRegistrationsColumnFilters,
      state.setColumnFilters,
      state.setInactiveRegistrationsColumnFilters,
      state.setUnregisteredPropertiesColumnFilters,
      state.unregisteredPropertiesColumnFilters,
    ],
  );
  useSyncSharedTextColumns({ buckets: sharedTextBuckets });
}

type ListColumnFilterSyncArgs = {
  columnFilters: Record<string, string[]>;
  setColumnFilters: Dispatch<SetStateAction<Record<string, string[]>>>;
  /** Column whose cells are the jurisdiction name. Omit when the list has none. */
  jurisdictionColumnId?: string;
  /** Column whose cells are the license type name. Omit when the list has none. */
  licenseTypeColumnId?: string;
  /** Column whose cells are the region name. Omit when the list has none. */
  regionColumnId?: string;
  selectedJurisdictions?: string[];
  setSelectedJurisdictions?: (ids: string[]) => void;
  jurisdictions?: readonly NamedIdOption[];
  selectedLicenseTypes?: string[];
  setSelectedLicenseTypes?: (ids: string[]) => void;
  licenseTypes?: readonly NamedIdOption[];
  selectedRegions?: string[];
  setSelectedRegions?: (ids: string[]) => void;
  regions?: readonly NamedIdOption[];
  adoptFromColumns?: boolean;
};

/**
 * Keep one list's column filters aligned with the header filters whose values
 * that list actually displays. A header filter with no matching column is left
 * alone.
 */
export function useListColumnFilterSync({
  columnFilters,
  setColumnFilters,
  jurisdictionColumnId,
  licenseTypeColumnId,
  regionColumnId,
  selectedJurisdictions = [],
  setSelectedJurisdictions,
  jurisdictions = [],
  selectedLicenseTypes = [],
  setSelectedLicenseTypes,
  licenseTypes = [],
  selectedRegions = [],
  setSelectedRegions,
  regions = [],
  adoptFromColumns = true,
}: ListColumnFilterSyncArgs) {
  const jurisdictionBuckets = useMemo(
    () =>
      jurisdictionColumnId
        ? [linkedBucket(jurisdictionColumnId, columnFilters, setColumnFilters)]
        : [],
    [columnFilters, jurisdictionColumnId, setColumnFilters],
  );
  const licenseTypeBuckets = useMemo(
    () =>
      licenseTypeColumnId
        ? [linkedBucket(licenseTypeColumnId, columnFilters, setColumnFilters)]
        : [],
    [columnFilters, licenseTypeColumnId, setColumnFilters],
  );
  const regionBuckets = useMemo(
    () =>
      regionColumnId
        ? [linkedBucket(regionColumnId, columnFilters, setColumnFilters)]
        : [],
    [columnFilters, regionColumnId, setColumnFilters],
  );

  useSyncGlobalColumnFilters({
    enabled: Boolean(jurisdictionColumnId && setSelectedJurisdictions),
    selectedIds: selectedJurisdictions,
    setSelectedIds: setSelectedJurisdictions ?? (() => undefined),
    options: jurisdictions,
    buckets: jurisdictionBuckets,
    adoptFromColumns,
  });
  useSyncGlobalColumnFilters({
    enabled: Boolean(licenseTypeColumnId && setSelectedLicenseTypes),
    selectedIds: selectedLicenseTypes,
    setSelectedIds: setSelectedLicenseTypes ?? (() => undefined),
    options: licenseTypes,
    buckets: licenseTypeBuckets,
    adoptFromColumns,
  });
  useSyncGlobalColumnFilters({
    enabled: Boolean(
      regionColumnId && setSelectedRegions && regions.length > 0,
    ),
    selectedIds: selectedRegions,
    setSelectedIds: setSelectedRegions ?? (() => undefined),
    options: regions,
    buckets: regionBuckets,
    adoptFromColumns,
  });
}
